"use client";
import { useEffect, useId, useRef, useState, type KeyboardEvent } from "react";
import { useTranslation } from "@/hooks/localization/useTranslations";
import SvgPCMenuArrow from "../../menu/pc/SvgPCMenuArrow";
import type { ISearchCategoryOption } from "./advanced-search-helpers";

interface Props {
  categories: ISearchCategoryOption[];
  value: string;
  onChange: (slug: string) => void;
  onOpen: () => void;
}

const ITEM_SELECTOR = '[role="menuitemradio"]';

// Category picker styled like the header dropdown menus (a native <select> can't
// be themed). Menu-button pattern: arrows move between options, Escape closes.
const AdvancedSearchCategoryMenu: React.FC<Props> = ({
  categories,
  value,
  onChange,
  onOpen,
}) => {
  const t = useTranslation();
  const menuId = useId();
  const rootRef = useRef<HTMLDivElement>(null);
  const toggleRef = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);

  const options: ISearchCategoryOption[] = [
    { slug: "", name: t.search_all_categories },
    ...categories,
  ];
  const selected = options.find(({ slug }) => slug === value) ?? options[0];

  useEffect(() => {
    const root = rootRef.current;
    if (!open || !root) return;
    const items = Array.from(
      root.querySelectorAll<HTMLButtonElement>(ITEM_SELECTOR),
    );
    (
      items.find((item) => item.getAttribute("aria-checked") === "true") ??
      items[0]
    )?.focus();

    const handlePointerDown = (event: PointerEvent) => {
      if (!root.contains(event.target as Node)) setOpen(false);
    };
    const handleFocusOut = (event: FocusEvent) => {
      const next = event.relatedTarget as Node | null;
      if (next && !root.contains(next)) setOpen(false);
    };
    document.addEventListener("pointerdown", handlePointerDown);
    root.addEventListener("focusout", handleFocusOut);
    return () => {
      document.removeEventListener("pointerdown", handlePointerDown);
      root.removeEventListener("focusout", handleFocusOut);
    };
  }, [open]);

  const toggle = (nextOpen: boolean) => {
    if (nextOpen) onOpen();
    setOpen(nextOpen);
  };

  const select = (slug: string) => {
    setOpen(false);
    toggleRef.current?.focus();
    onChange(slug);
  };

  const handleMenuKeyDown = (event: KeyboardEvent<HTMLUListElement>) => {
    if (event.key === "Escape") {
      event.stopPropagation();
      setOpen(false);
      toggleRef.current?.focus();
      return;
    }
    const keys = ["ArrowDown", "ArrowUp", "Home", "End"];
    if (!keys.includes(event.key)) return;
    event.preventDefault();
    const items = Array.from(
      event.currentTarget.querySelectorAll<HTMLButtonElement>(ITEM_SELECTOR),
    );
    const index = items.indexOf(document.activeElement as HTMLButtonElement);
    const last = items.length - 1;
    const next =
      event.key === "Home"
        ? 0
        : event.key === "End"
          ? last
          : event.key === "ArrowDown"
            ? (index + 1) % items.length
            : (index - 1 + items.length) % items.length;
    items[Math.min(Math.max(next, 0), last)]?.focus();
  };

  return (
    <div ref={rootRef} className="advanced-search__category">
      <button
        ref={toggleRef}
        type="button"
        className="advanced-search__category-toggle"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        aria-label={`${t.search_category}: ${selected.name}`}
        onClick={() => toggle(!open)}
        onKeyDown={(event) => {
          if (event.key === "ArrowDown" && !open) {
            event.preventDefault();
            toggle(true);
          }
        }}
      >
        <span className="advanced-search__category-label">{selected.name}</span>
        <SvgPCMenuArrow rotate={open} />
      </button>
      {open && (
        <div className="advanced-search__menu">
          <ul
            id={menuId}
            role="menu"
            aria-label={t.search_category}
            className="advanced-search__menu-list"
            onKeyDown={handleMenuKeyDown}
          >
            {options.map(({ slug, name }) => {
              const isSelected = slug === selected.slug;
              return (
                <li key={slug || "all"} role="none">
                  <button
                    type="button"
                    role="menuitemradio"
                    aria-checked={isSelected}
                    className="advanced-search__menu-item"
                    onClick={() => select(slug)}
                  >
                    <span className="advanced-search__menu-item-name">
                      {name}
                    </span>
                    {isSelected && (
                      <svg
                        className="advanced-search__menu-check"
                        aria-hidden="true"
                        focusable="false"
                        width="16"
                        height="16"
                        viewBox="0 0 16 16"
                        fill="none"
                      >
                        <path
                          d="M3 8.5 6.5 12 13 4.5"
                          stroke="currentColor"
                          strokeWidth="1.75"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
};

export default AdvancedSearchCategoryMenu;
